// class takes an AcceleratorBatch.uuid, deletes any document linked to this Batch Instance, then
// loads the AcceleratorDocs from the AcceleratorBatch into the AccDocStore
import { stringifyKey } from "../decorators/StoreIdHelper";
import { AcceleratorBatch } from "../models/AcceleratorBatch";
import { AcceleratorBatchSource } from "../models/AcceleratorBatchSource";
import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../models/AcceleratorDoc";
import { AcceleratorDocMetadata } from "../models/AcceleratorDocMetadata";
import API from "../models/API";
import { BatchClassConfig } from "../models/config/BatchClassConfig";
import {
  removeBadReuseTableModels,
  updateExtractionResultsFromTableModel,
} from "../models/extraction/ExtractedTableCleanup";
import { Hydratable } from "../models/hydration/Hydratable";
import { HydratableFactory } from "../models/hydration/HydratableFactory";
import Model from "../models/Model";
import { AcceleratorBatchStore } from "../stores/impl/AcceleratorBatchStore";
import { AcceleratorDocMetadataStore } from "../stores/impl/AcceleratorDocMetadataStore";
import { AcceleratorDocModelStore } from "../stores/impl/AcceleratorDocModelStore";
import { AcceleratorDocStore } from "../stores/impl/AcceleratorDocStore";
import { ModelStore } from "../stores/impl/ModelStore";
import { AcceleratorDocExtractor } from "./AcceleratorDocExtractor";

class AcceleratorBatchDocumentLoaderClass {
  static PARALLEL_DOCUMENT_PROMISES = 4;

  setDocMetadata(metadata: AcceleratorDocMetadata) {
    AcceleratorDocMetadataStore.set(metadata, true);
  }

  setDocMetadataFromDoc(doc: AcceleratorDoc, batchName: string) {
    const existing = AcceleratorDocMetadataStore.getWithoutLookup(doc.storeId);
    let classifiedModelName = doc.modelName;
    if (
      classifiedModelName === "" &&
      (doc.classifiedModel?.templateType === "GENERAL" ||
        doc.classifiedModel?.templateType === "FORMAT" ||
        existing?.wasGeneral ||
        existing?.wasGeneralFormat)
    ) {
      classifiedModelName =
        doc.classifiedModel?.name || existing?.classifiedModelName || "";
    }

    const metadata = new AcceleratorDocMetadata({
      batchInstanceId: doc.batchInstanceId,
      classifiedDocType: doc.classifiedDocType,
      pageCount: doc.pages.length,
      classifiedModelName,
      source: doc.source,
      statuses: doc.statuses,
      uuid: doc.uuid,
      batchInstanceUuid: doc.storeId.batchInstanceUuid,
      docId: doc.storeId.docId,
      batchName: batchName,
    });
    metadata.batchClassId = doc.getBatchClassId();
    AcceleratorDocMetadataStore.set(metadata, true);
  }

  /*
   * Expects AcceleratorBatchStore to be populated with AcceleratorBatch for acceleratorBatchUuid.
   */
  loadDoc(
    accDocKey: AcceleratorDocStoreKey,
    acceleratorBatchUuid: string,
    batchClassConfig: BatchClassConfig,
  ): Hydratable<AcceleratorDoc, AcceleratorDocStoreKey> {
    const timeKey: string = `AcceleratorBatchDocumentLoader.loadDoc(${acceleratorBatchUuid}, ${batchClassConfig.batchClassId})`;

    // get metadata so stale property can be set. assume exists :O
    const accDocMetadata =
      AcceleratorDocMetadataStore.getWithoutLookup(accDocKey);

    const accBatch =
      AcceleratorBatchStore.getWithoutLookup(acceleratorBatchUuid);
    if (!accBatch) {
      console.warn(`Could not find AcceleratorBatch ${acceleratorBatchUuid}`);
      accDocMetadata.stale = true;
      AcceleratorDocMetadataStore.set(accDocMetadata);
      return HydratableFactory.getAcceleratorDoc(async () => undefined, {
        batchInstanceUuid: "",
        docId: "",
      });
    }

    // acceleratorDocMetadata should already be updated with AccBatch data
    // still requires Model and Classification things updated

    // set accDoc/accDocMetadata stuff here
    if (accDocMetadata) {
      // FIXME: should this be called earlier in the life cycle , or is this already superseeded by BatchService ?
      accDocMetadata?.updateDataFromBatchAndModelInfo(
        accBatch!,
        batchClassConfig.batchClassId,
      );
      AcceleratorDocMetadataStore.set(accDocMetadata, true);
    }

    return HydratableFactory.getAcceleratorDoc(async () => {
      // console.time(timeKey)
      let accDoc = undefined;
      try {
        accDoc = await AcceleratorDocStore.lookup(accDocKey);
      } catch (e) {
        console.warn(
          `Could not load AcceleratorDoc ${stringifyKey(accDocKey)}`,
        );
        if (e === "stale") {
          AcceleratorDocMetadataStore.markAllDocMetadataStale(
            accDocKey.batchInstanceUuid,
          );
        }
      }
      if (!accDoc) {
        accDocMetadata.loadingError = true;
        AcceleratorDocMetadataStore.set(accDocMetadata, true);
        // AcceleratorDocMetadataStore.markAllDocMetadataErrorLoading(accDocKey.batchInstanceUuid);
        return undefined;
      }

      AcceleratorDocStore.set(accDoc, true);
      await this.postDocumentLoad(
        accDoc,
        accBatch!.source,
        accBatch!.batchInstanceName,
        batchClassConfig,
      );

      // console.timeEnd(timeKey)
      return AcceleratorDocStore.getWithoutLookup(accDocKey);
    }, accDocKey);
  }

  async reprocess(acceleratorBatchUuid: string): Promise<boolean> {
    this.removeBatchData(acceleratorBatchUuid /*, batchClassConfig*/);

    const response = await API.patch(
      `/v0/instance/${acceleratorBatchUuid}/accelerate`,
    )
      .then((x) => {
        if (x.ok) {
          return x.json() as Promise<AcceleratorBatch>;
        }
        console.log(
          `Reprocess batch ${acceleratorBatchUuid} invalid ${x.status}:${x.statusText}\n`,
          x,
        );
        return null;
      })
      .catch((e) => {
        console.log("Could not reprocess batch", e);
        return false;
      });

    return response != null && response != false;
  }

  async delete(acceleratorBatchUuid: string): Promise<boolean> {
    this.removeBatchData(acceleratorBatchUuid /*, batchClassConfig*/);

    const response = API.delete(`/v0/instance/${acceleratorBatchUuid}`)
      .then((value: Response) => value.json())
      .catch(() => {
        return false;
      });

    // await this.load(acceleratorBatchUuid, batchClassConfig);

    return response;
  }

  /**
   * Remove data from AcceleratorBatchStore, AcceleratorDocStore, AcceleratorDocMetadataStore
   */
  public removeBatchData(acceleratorBatchUuid: string) {
    const accBatch = AcceleratorBatchStore.remove(acceleratorBatchUuid);
    if (accBatch) {
      console.debug(`Removed batch data ${accBatch.batchInstanceId}`);
      // remove AcceleratorDocModelStore associations
      // delete AcceleratorDocMetadata
      const mDatas =
        AcceleratorDocMetadataStore.getByBatchInstanceUuid(
          acceleratorBatchUuid,
        );
      console.debug(
        `Removing ${mDatas.length} metadata by batch instance uuid`,
      );
      mDatas.forEach((x) => {
        AcceleratorDocMetadataStore.removeByValue(x);
      });
      // accBatch was already loaded, remove the AccDocs that may have been stored
      const accDocs = AcceleratorDocStore.getByBatchInstanceUuid(
        /*batchClassConfig,*/ acceleratorBatchUuid,
      );
      console.debug(`Removing ${accDocs.length} docs by batch instance uuid`);
      accDocs.forEach((x) => AcceleratorDocStore.removeByValue(x));
      // remove acceleratorDocModelStore associations

      console.debug(`Removing model store association for each doc`);
      accDocs.forEach((doc) => {
        const model = AcceleratorDocModelStore.getModel(doc);
        if (model) {
          console.log(
            `Removing MDL:${stringifyKey(model.storeId)} from DOC:${stringifyKey(doc.storeId)}`,
          );
          AcceleratorDocModelStore.removeAssociation(
            doc.storeId,
            model.storeId,
          );
        } else {
          console.debug(
            `Not removing model ${doc.modelName} as it does not exist in doc model store`,
          );
        }
      });
    }
    return;
  }

  public async postDocumentLoad(
    doc: AcceleratorDoc,
    source: AcceleratorBatchSource,
    batchName: string,
    batchClassConfig: BatchClassConfig,
  ): Promise<void> {
    // source Source
    doc.setAcceleratorBatchSource(source);
    // independently mutable ModelData
    const modelData = { ...doc.classifiedModel };
    // console.debug('Is Model Unknown?')
    if (modelData.templateType === "UNKNOWN") {
      // console.debug('Model Unknown, changing name to ""');
      modelData.name = "";
      modelData.created = undefined;
      modelData.lastModified = undefined;
    }
    if (
      modelData.docType === "Unknown" &&
      modelData.docType !== doc.classifiedDocType
    ) {
      // console.debug(`Model Unknown, and AcceleratorDoc autoclassified, setting Model docType to ${doc.classifiedDocType}`);
      // reivew, changeDocType should be responsbile for this right ?
      modelData.docType = doc.classifiedDocType;
    } else {
      // TODO: test that this doesn't break the dlfs or tables Maps. probably doesnt.
      // console.debug(`Setting AcceleratorDoc docType to ${modelData.docType} from Model`);
      doc.classifiedDocType = modelData.docType;
    }

    // console.debug(`Checking if ModelData should be modified for being General`);
    if (
      modelData.templateType === "GENERAL" ||
      modelData.templateType === "FORMAT"
    ) {
      // console.debug('Converting General Model to not General model on Document load');
      modelData.templateType = "NORMAL";
      modelData.documentStartRules = [];
      modelData.documentEndRules = [];
      modelData.name = "";
      modelData.created = undefined;
      modelData.lastModified = undefined;
    }
    // if Doc model was unknown or general, update AccDocs name to "" from ModelName if required.
    doc.modelName = modelData.name;

    // the following block ensures that a valid DocTypeConfig is set on the AcceleatorDoc.
    let docTypeConfig = batchClassConfig.getDocumentType(doc.classifiedDocType);
    if (!docTypeConfig) {
      // console.debug("DocTypeConfig on AcceleratorDoc was not valid");
      docTypeConfig = batchClassConfig.docTypes[0]!;
    }

    // initialize Model !
    // check if model already exists
    const existingModelKey = {
      batchClassIdentifier: doc.getBatchClassId(),
      name: doc.getModelName(),
      docType: doc.classifiedDocType,
    };
    const existingModel = ModelStore.getWithoutLookup(existingModelKey);
    if (existingModel) {
      const model = existingModel;
      // check if model versions are up to date
      if (
        !model.unknown &&
        (doc.classifiedModel.version ?? 0) > model.version
      ) {
        // console.info(`Model ${getObjectStoreIdString(model)}.v${model.version} Is Stale, received v${doc.classifiedModel.version}`);
        model.knownHigherVersion = doc.classifiedModel.version;
      }

      const extractor = new AcceleratorDocExtractor(
        doc,
        model!,
        docTypeConfig,
        batchClassConfig.acceleratorOcrThresholds,
      );
      await extractor.extract();

      // remove duplicate reuse results
      doc.dlfs.forEach((docFieldData) => {
        docFieldData.forEach((efd) => {
          // efd.clearDuplicateReuseResults();
        });
      });

      doc.calculateDocumentStatusWithOcr(
        batchClassConfig.acceleratorOcrThresholds,
        model,
        docTypeConfig,
      );
      AcceleratorDocModelStore.setModel(doc.storeId, model);
      this.setDocMetadataFromDoc(doc, batchName);
      return;
    }

    const model = new Model(modelData);

    // this will ensure that an entry exists for all fields and tables for the current DocType.
    // this is required for when the above block changes the DocTypeConfig, and when a DocTypeConfig has been
    // modified after this AccDoc was processed.
    doc.changeDocType(docTypeConfig, model);

    // validate the Model has all the necessary DLFs
    model.changeDocType(docTypeConfig);

    // if the Document was a general, or unknown, force the Model to be unknown (clear general info)
    // and remove the ERROR dlfs from the generalModels DLF rules )
    if (doc.classifiedModel.templateType !== "NORMAL") {
      model.setTemplateType("NORMAL");
      doc.replaceErrorFieldsWithBlankOrReuse(docTypeConfig);
      // replace DLF Models with data from Doc
      doc.updateModelDlfsFromExtractionResults(docTypeConfig, model);
      // replace
      // doc.updateExtractionResultsFromTableModel(docTypeConfig, model);

      // remove bad table reuse entries
      updateExtractionResultsFromTableModel(doc, docTypeConfig, model);
    }

    removeBadReuseTableModels(doc, docTypeConfig, model);

    // run extraction incase the model is newer than what extracted this
    if (!model.unknown && !model.general) {
      // console.debug(`Running required Extraction on AcceleratorDoc ${getObjectStoreIdString(doc)} with Model ${getObjectStoreIdString(model)}`);
      // console.log(doc.getExtractedFieldData('testField').getExtracted());
      // console.log(model.getDlfMatcher('testField'));
      // await doc.runRequiredExtraction(model, docTypeConfig);
      await new AcceleratorDocExtractor(
        doc,
        model,
        docTypeConfig,
        batchClassConfig.acceleratorOcrThresholds,
      ).extract();
    }

    // remove duplicate reuse results
    doc.dlfs.forEach((docFieldData) => {
      docFieldData.forEach((efd) => {
        // efd.clearDuplicateReuseResults();
      });
    });

    // make model not changed
    model.setChanged(false);
    // calculate statuses
    doc.calculateDocumentStatusWithOcr(
      batchClassConfig.acceleratorOcrThresholds,
      model,
      docTypeConfig,
    );
    AcceleratorDocModelStore.setModel(doc.storeId, model);
    this.setDocMetadataFromDoc(doc, batchName);
  }
}

const AcceleratorBatchDocumentLoader =
  new AcceleratorBatchDocumentLoaderClass();

export { AcceleratorBatchDocumentLoader };
